// src/queries/useOrganizations.ts
import { API_ENDPOINTS } from '@/api/endpoints';
import { useAuthStatus } from '@/auth/useAuthStatus';
import { Department } from '@/types/department';
import { Organization } from '@/types/organization';
import { User } from '@/types/user';
import { apiFetch } from '@/util/apiClient';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface CreateOrganizationData {
  name: string;
  description: string;
}

// Fetch organizations
export function useOrganizations(includeInactive = false) {
  const { isAuthenticated } = useAuthStatus();

  return useQuery({
    queryKey: ['organizations', { includeInactive }],
    queryFn: () =>
      apiFetch<Organization[]>(
        `${API_ENDPOINTS.ORGANIZATIONS.LIST}?includeInactive=${includeInactive}`,
      ),
    enabled: isAuthenticated,
  });
}

// Fetch organizations the current user is a member of
export function useUserOrganizations() {
  const { isAuthenticated } = useAuthStatus();

  return useQuery({
    queryKey: ['user-organizations'],
    queryFn: () => apiFetch<Organization[]>(API_ENDPOINTS.ORGANIZATIONS.LIST),
    staleTime: 60_000,
    // Only run this query if we have a logged-in user
    enabled: isAuthenticated,
  });
}

// Create organization
export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOrganizationData) =>
      apiFetch<Organization>(API_ENDPOINTS.ORGANIZATIONS.CREATE, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

// Delete organization
export function useDeleteOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(API_ENDPOINTS.ORGANIZATIONS.DELETE(id), {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

export function useRestoreOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(API_ENDPOINTS.ORGANIZATIONS.RESTORE(id), {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

export function useOrganizationUsers(orgId?: string) {
  const { isAuthenticated } = useAuthStatus();

  return useQuery({
    queryKey: ['organization', orgId, 'users'],
    queryFn: () => apiFetch<User[]>(API_ENDPOINTS.ORGANIZATIONS.USERS(orgId!)),
    enabled: Boolean(orgId) && isAuthenticated,
    staleTime: 60_000,
  });
}

export function useOrganizationDepartments(orgId?: string) {
  const { isAuthenticated } = useAuthStatus();

  return useQuery({
    queryKey: ['organization', orgId, 'departments'],
    queryFn: () =>
      apiFetch<Department[]>(API_ENDPOINTS.ORGANIZATIONS.DEPARTMENTS(orgId!)),
    enabled: Boolean(orgId) && isAuthenticated,
    staleTime: 60_000,
  });
}

interface InviteData {
  email: string;
  role?: string;
  sendEmail?: boolean;
}

export interface InviteResponse {
  code: string;
  inviteUrl: string;
  email: string;
  emailSent: boolean;
  expiresInDays: number;
}

export interface Invitation {
  id: string;
  code: string;
  email: string;
  role: string;
  createdBy: number;
  createdByName: string;
  createdAt: string;
  usedBy: number | null;
  usedByName: string | null;
  usedAt: string | null;
  isExpired: boolean;
  isValid: boolean;
  signupUrl: string;
}

export function useCreateInvite(orgId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: InviteData) => {
      if (!orgId) {
        throw new Error('Organization ID is required');
      }
      return apiFetch<InviteResponse>(
        API_ENDPOINTS.ORGANIZATIONS.INVITES(orgId),
        {
          method: 'POST',
          body: JSON.stringify({
            email: data.email,
            sendEmail: data.sendEmail ?? true,
            role: data.role,
          }),
        },
      );
    },
    onSuccess: () => {
      // Invalidate invitations list to refresh after creating new invite
      void queryClient.invalidateQueries({
        queryKey: ['organization', orgId, 'invitations'],
      });
    },
  });
}

export function useOrganizationInvitations(orgId?: string) {
  const { isAuthenticated } = useAuthStatus();

  return useQuery({
    queryKey: ['organization', orgId, 'invitations'],
    queryFn: () =>
      apiFetch<Invitation[]>(API_ENDPOINTS.ORGANIZATIONS.INVITES(orgId!)),
    enabled: Boolean(orgId) && isAuthenticated,
    staleTime: 30_000, // Refresh every 30 seconds
  });
}
